import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Settings, ToggleLeft, ToggleRight, Plus, Trash2, Save, Weight, FileText, Sparkles
} from 'lucide-react';

const SECTION_LABELS = {
  communication_analysis: 'Communication Analysis',
  tone_analysis: 'Tone Analysis',
  emotion_analysis: 'Emotion Analysis',
  leadership_assessment: 'Leadership Assessment',
  eq_assessment: 'EQ Assessment',
  coaching_recommendations: 'Coaching Recommendations',
  emotional_timeline: 'Emotional Timeline',
  speech_analytics: 'Speech Analytics',
  ai_coach_summary: 'AI Coach Summary',
  benchmarks: 'Benchmarks',
  frameworks: 'Frameworks',
};

const WEIGHT_LABELS = {
  communication_clarity: 'Communication Clarity',
  empathy_eq: 'Empathy & EQ',
  problem_solving: 'Problem Solving',
  professionalism: 'Professionalism',
  outcome_effectiveness: 'Outcome Effectiveness',
  tone_vocal_presence: 'Tone & Vocal Presence',
  emotion_management: 'Emotion Management',
};

export default function ReportBuilder() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState({});
  const [weights, setWeights] = useState({});
  const [customComps, setCustomComps] = useState([]);
  const [showAddComp, setShowAddComp] = useState(false);
  const [newComp, setNewComp] = useState({ name: '', description: '', weight: 10, behavioral_indicators: [''] });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axiosAuth().get('/admin/report-config');
      const c = res.data.config;
      setSections(c.sections || {});
      setWeights(c.scoring_weights || {});
      setCustomComps(c.custom_competencies || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load config');
    } finally { setLoading(false); }
  }, [axiosAuth, navigate]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggleSection = (key) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const updateWeight = (key, val) => {
    setWeights(prev => ({ ...prev, [key]: Math.max(0, Math.min(100, parseInt(val) || 0)) }));
  };

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  const saveConfig = async () => {
    if (totalWeight !== 100) {
      toast.error(`Weights must sum to 100 (currently ${totalWeight})`);
      return;
    }
    setSaving(true);
    try {
      await axiosAuth().put('/admin/report-config', {
        sections,
        scoring_weights: weights,
      });
      toast.success('Report configuration saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addCompetency = async () => {
    if (!newComp.name.trim()) return toast.error('Name is required');
    try {
      const indicators = newComp.behavioral_indicators.filter(i => i.trim());
      await axiosAuth().post('/admin/report-config/competencies', {
        name: newComp.name.trim(),
        description: newComp.description.trim(),
        weight: newComp.weight,
        behavioral_indicators: indicators,
      });
      toast.success('Competency added');
      setNewComp({ name: '', description: '', weight: 10, behavioral_indicators: [''] });
      setShowAddComp(false);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const deleteCompetency = async (compId) => {
    if (!window.confirm('Delete this competency?')) return;
    try {
      await axiosAuth().delete(`/admin/report-config/competencies/${compId}`);
      toast.success('Competency deleted');
      fetchConfig();
    } catch { toast.error('Failed to delete'); }
  };

  const addIndicatorField = () => {
    setNewComp(p => ({ ...p, behavioral_indicators: [...p.behavioral_indicators, ''] }));
  };

  const updateIndicator = (idx, val) => {
    setNewComp(p => {
      const arr = [...p.behavioral_indicators];
      arr[idx] = val;
      return { ...p, behavioral_indicators: arr };
    });
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="report-builder-page">
      <Navbar showBack backTo="/admin" backLabel="Report Builder" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Report Builder</h1>
            <p className="font-body text-sm text-muted-foreground mt-1">Configure assessment dimensions, report sections, and scoring weights</p>
          </div>
          <Button data-testid="save-config-btn" onClick={saveConfig} disabled={saving || totalWeight !== 100}
            className="bg-brand text-[#333333] gap-1.5 h-9 px-4 text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50">
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Save Configuration
          </Button>
        </div>

        {/* Report Sections Toggle */}
        <div className="heritage-card p-5 mb-6" data-testid="report-sections">
          <h2 className="font-heading text-sm font-bold mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" /> Report Sections
          </h2>
          <p className="font-body text-xs text-muted-foreground mb-3">Enable or disable sections in the performance report</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(SECTION_LABELS).map(([key, label]) => {
              const enabled = sections[key] !== false;
              return (
                <button key={key} data-testid={`toggle-${key}`}
                  onClick={() => toggleSection(key)}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${enabled ? 'border-brand/30 bg-brand/5' : 'border-border bg-muted/30 opacity-60'}`}>
                  {enabled ? <ToggleRight className="w-5 h-5 text-brand flex-shrink-0" /> : <ToggleLeft className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                  <span className="font-body text-xs font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scoring Weights */}
        <div className="heritage-card p-5 mb-6" data-testid="scoring-weights">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-sm font-bold flex items-center gap-2">
              <Weight className="w-4 h-4 text-muted-foreground" /> Scoring Weights
            </h2>
            <Badge className={`${totalWeight === 100 ? 'bg-success/10 text-success' : 'bg-accent/10 text-accent'} border-none text-xs px-2 py-0.5`} data-testid="weight-total">
              Total: {totalWeight}%{totalWeight !== 100 ? ' (must be 100%)' : ''}
            </Badge>
          </div>
          <p className="font-body text-xs text-muted-foreground mb-3">Adjust how much each dimension contributes to the overall score</p>
          <div className="space-y-3">
            {Object.entries(WEIGHT_LABELS).map(([key, label]) => {
              const val = weights[key] || 0;
              return (
                <div key={key} className="flex items-center gap-3" data-testid={`weight-${key}`}>
                  <span className="font-body text-xs font-medium w-40 flex-shrink-0">{label}</span>
                  <input type="range" min="0" max="50" value={val}
                    onChange={(e) => updateWeight(key, e.target.value)}
                    className="flex-1 h-1.5 bg-muted rounded-full appearance-none cursor-pointer accent-brand" />
                  <div className="flex items-center gap-1 w-16 flex-shrink-0">
                    <input type="number" min="0" max="100" value={val}
                      onChange={(e) => updateWeight(key, e.target.value)}
                      data-testid={`weight-input-${key}`}
                      className="w-12 h-7 text-center text-xs font-bold border border-border rounded-md bg-surface" />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Competencies */}
        <div className="heritage-card p-5 mb-6" data-testid="custom-competencies">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-sm font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-muted-foreground" /> Custom Competencies
            </h2>
            <Button data-testid="add-competency-btn" onClick={() => setShowAddComp(true)}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Add Competency
            </Button>
          </div>
          <p className="font-body text-xs text-muted-foreground mb-3">Define organization-specific competencies with behavioral indicators</p>

          {customComps.length === 0 ? (
            <div className="text-center py-6 bg-muted/30 rounded-xl">
              <Sparkles className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
              <p className="font-body text-xs text-muted-foreground">No custom competencies yet</p>
              <p className="font-body text-[10px] text-muted-foreground mt-0.5">Examples: Customer Obsession, Innovation Mindset, People Leadership</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customComps.map((c) => (
                <div key={c.id} className="bg-muted/30 rounded-xl p-4 border border-border/50" data-testid={`competency-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-xs font-bold">{c.name}</span>
                      <Badge className="bg-brand/10 text-brand border-none text-[10px] px-1.5 py-0">Weight: {c.weight}%</Badge>
                    </div>
                    <button onClick={() => deleteCompetency(c.id)} data-testid={`delete-comp-${c.id}`}
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {c.description && <p className="font-body text-[11px] text-muted-foreground mb-2">{c.description}</p>}
                  {c.behavioral_indicators?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.behavioral_indicators.map((ind) => (
                        <Badge key={`bi-${ind.slice(0,20)}`} className="bg-surface text-muted-foreground border border-border text-[9px] px-1.5 py-0">{ind}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Custom Competency Dialog */}
      <Dialog open={showAddComp} onOpenChange={setShowAddComp}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="add-competency-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Add Custom Competency</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Define an organization-specific competency with behavioral indicators</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Competency Name</label>
              <input data-testid="comp-name-input" type="text" value={newComp.name}
                onChange={(e) => setNewComp(p => ({ ...p, name: e.target.value }))}
                className="heritage-input w-full text-sm" placeholder="e.g., Customer Obsession" />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Description</label>
              <textarea data-testid="comp-desc-input" value={newComp.description}
                onChange={(e) => setNewComp(p => ({ ...p, description: e.target.value }))}
                className="heritage-input w-full text-sm min-h-[60px]" placeholder="What does this competency measure?" />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Weight (%)</label>
              <input data-testid="comp-weight-input" type="number" min="1" max="50" value={newComp.weight}
                onChange={(e) => setNewComp(p => ({ ...p, weight: parseInt(e.target.value) || 10 }))}
                className="heritage-input w-24 text-sm" />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Behavioral Indicators</label>
              <div className="space-y-1.5">
                {newComp.behavioral_indicators.map((ind, i) => (
                  <input key={`indicator-${i}`} type="text" value={ind}
                    onChange={(e) => updateIndicator(i, e.target.value)}
                    className="heritage-input w-full text-sm" placeholder={`Indicator ${i + 1}`} />
                ))}
              </div>
              <button onClick={addIndicatorField} className="font-body text-xs text-brand hover:underline mt-1">+ Add indicator</button>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowAddComp(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="save-competency-btn" onClick={addCompetency} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Add Competency</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
